import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import WebTileLayer from "@arcgis/core/layers/WebTileLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import ElevationLayer from "@arcgis/core/layers/ElevationLayer";
import TileLayer from "@arcgis/core/layers/TileLayer";
import geometryTools from "utils/geometryTools";
import Basemap from "@arcgis/core/Basemap";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import UserGroup from "components/ZoomTo/UserGroup";
import GetArcGISToken from "utils/GetArcGISToken";
import config from "config";

const LAYER_API_CLASS = {
  feature: FeatureLayer,
  "vector-tile": VectorTileLayer,
  "map-image": MapImageLayer,
  "web-tile": WebTileLayer,
  "tile-image": TileLayer,
  elevation: ElevationLayer,
};

const SLOPE_RENDERER = {
  type: "unique-value",
  field: "COMB_TYPE",
  uniqueValueInfos: [
    {
      symbol: {
        type: "simple-fill",
        color: [0, 0, 0, 0],
        outline: {
          color: [152, 230, 0, 255],
          width: 5,
        },
      },
      value: "Cut",
      label: "Cut slopes",
    },
    {
      symbol: {
        type: "simple-fill",
        color: [0, 0, 0, 0],
        outline: {
          color: [115, 255, 223, 255],
          width: 5,
        },
      },
      value: "DT",
      label: "Disturbed terrain",
    },
    {
      symbol: {
        type: "simple-fill",
        color: [0, 0, 0, 0],
        outline: {
          color: [115, 178, 255, 255],
          width: 5,
        },
      },
      value: "Fill",
      label: "Fill slopes",
    },
    {
      symbol: {
        type: "simple-fill",
        color: [0, 0, 0, 0],
        outline: {
          color: [233, 255, 190, 255],
          width: 5,
        },
      },
      value: "ND",
      label: "NT defence measures",
    },
    {
      symbol: {
        type: "simple-fill",
        color: [0, 0, 0, 0],
        outline: {
          color: [235, 179, 252, 255],
          width: 5,
        },
      },
      value: "NS",
      label: "NT stabilisation measures",
    },
    {
      symbol: {
        type: "simple-fill",
        color: [0, 0, 0, 0],
        outline: {
          color: [168, 0, 0, 255],
          width: 3,
        },
      },
      value: "Wall",
      label: "Retaining walls",
    },
  ],
};

let createLayerBaseArgs = {
  visible: false,
  spatialReference: new geometryTools.SpatialReference({
    wkid: 2326,
  }),
};
let creatLayerFunc = function(i, is3DView, baseArgs = createLayerBaseArgs) {
  var args = { ...baseArgs, ...(i.otherArgs || {}) };
  if ("id" in i) args.portalItem = { id: i.id };
  else args.url = i.url;

  if (i.has3D && !!args.url && is3DView) {
    let lastSlash = args.url.lastIndexOf("/");
    args.url =
      args.url.substr(0, lastSlash) + "_3D" + args.url.substr(lastSlash);
  }

  if (i.layerId) args.layerId = i.layerId;
  if (i.title) args.title = i.title;
  var layer = new LAYER_API_CLASS[i.lyType || i.type](args);
  layer.popupEnabled = false;
  layer.catch(() => {});
  return layer;
};

let addAvailableLayers = function(mapView, is3DView) {
  this.$config[this.layerListName] = JSON.parse(
    JSON.stringify(this.$config.AvailLayerList)
  );
  let AvailLayerList = this.$config[this.layerListName];

  this.$config.SLOPE_RENDERER = SLOPE_RENDERER;

  AvailLayerList.overlays
    .filter((i) => i.ordered)
    .forEach((i, j) => (i.seq = j - 1));

  let elayer = creatLayerFunc(AvailLayerList.elevation);
  mapView.map.ground.layers.add(elayer);

  let mmsQueryLayer = creatLayerFunc(AvailLayerList.mmsQueryLayer, is3DView);
  AvailLayerList.mmsQueryLayer.layer = mmsQueryLayer;
  mapView.map.add(mmsQueryLayer);

  AvailLayerList.basemaps = AvailLayerList.basemaps.filter(
    (i) => i.mode === `${is3DView ? 3 : 2}d`
  );

  let basemap = AvailLayerList.basemaps.reduce((p, i, idx) => {
    console.debug(i);
    i.layer = creatLayerFunc(i, is3DView, {});
    i.layer.visible = idx === 0;
    if (is3DView && i.properties3D)
      for (var property in i.properties3D)
        i.layer.set(property, i.properties3D[property]);
    p.push(i.layer);
    return p;
  }, []);
  mapView.map.basemap = new Basemap({
    baseLayers: basemap,
  });

  let vm = this;
  basemap[0].catch((e) => {
    {
      console.debug("Cannot load basemap", e);
      vm.props.showDialog({
        type: "error",
        isOpen: true,
        title: "Unable to load the baesemap",
        content:
          "Unable to load the basemap(s). Some of the functions of this application will be unavailable",
        onDialogOk: () => {},
      });
    }
  });
  let basemapLoadedPromise = new Promise((resolve) => {
    basemap[0].when(() => {
      resolve();
    });
  });

  mapView.map.layers.add(
    new geometryTools.GroupLayer({
      title: "3D Layers",
    })
  );

  // We don't need too many map layers in compact mode
  if (this.props.isCompactMode) {
    let slopeLyrInfo = AvailLayerList.overlays.find(
      (i) => i.title == "Man Made Slope"
    );
    let layer = creatLayerFunc(slopeLyrInfo, is3DView);
    mapView.map.add(layer);
    return basemapLoadedPromise;
  }

  var overlaysGroup = new geometryTools.GroupLayer({
    title: "Overlays",
  });
  var orderedLayerList = new geometryTools.GroupLayer({
    title: "Overlays Ordered Layers",
  });
  AvailLayerList.overlays.forEach((i) => {
    var layer = null;
    if (i.lyType == "group") {
      var layer = new geometryTools.GroupLayer({
        title: i.title,
        visible: false,
      });
      var childLayer = i.layers.map(
        (lyr) => ((lyr.layer = creatLayerFunc(lyr, is3DView)), lyr.layer)
      );
      childLayer[0].visible = true;
      layer.addMany(childLayer);
    } else layer = creatLayerFunc(i, is3DView);
    i.layer = layer;
    if ("seq" in i && i.seq >= 0) orderedLayerList.add(layer);
    else overlaysGroup.add(layer, 0);
  });
  overlaysGroup.add(orderedLayerList, 0);
  mapView.map.layers.add(overlaysGroup);

  var layer2DGroup = new geometryTools.GroupLayer({
    title: "2D Layers",
  });
  mapView.map.layers.add(layer2DGroup);

  AvailLayerList.layer2D = UserGroup[
    "Group" + GetArcGISToken.user.userGroup
  ].Schemas.map((i) => [i.Value, i.Text])
    .filter((i) => i[0] !== "UAV")
    .map((i) => ({
      title: i[1],
      url: `${config.serverUrl}/rest/services/GInfo/${i[0]}/MapServer`,
      type: `map-image`,
    }));
  AvailLayerList.layer2D.forEach((i, j) => {
    var layer = creatLayerFunc(i, is3DView);
    i.layer = layer;
    layer2DGroup.add(layer);
  });

  let mmsLayer = mapView.map.allLayers.find(
    (i) => i.title === "Man Made Slope"
  );
  modifyMMSSymbology(mmsLayer, mapView);

  if (is3DView) {
    let mmsLabelLayer = creatLayerFunc(
      {
        ...AvailLayerList.mmsQueryLayer,
        layerId: 69,
        title: "MMS - Feature Numbers",
      },
      is3DView
    );
    mapView.map.add(mmsLabelLayer);
    reactiveUtils.watch(
      () => mmsLayer.visible,
      (newV) => (mmsLabelLayer.visible = newV)
    );
  }

  return basemapLoadedPromise;
};

let modifyMMSSymbology = function(layer, view) {
  layer.when(() => {
    layer.allSublayers.items[0].popupEnabled = false;
    if (view.type === "3d") {
      layer.findSublayerById(44).renderer = SLOPE_RENDERER;
    }
    layer.sublayers.forEach((i) => {
      i.visible =
        i.id === 40 ||
        i.id === 44 ||
        (view.type === "2d" && (i.id === 68 || i.id === 69));
    });
  });
};

export { addAvailableLayers, creatLayerFunc };
